import React, { useEffect, useState, useRef } from "react";
import { Upload, FileText, Eye, EyeOff, X, Plus, Download, Users } from "lucide-react";
import { toast } from "../../../ui/Toast";

const API     = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card    = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls= "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls= "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const CATEGORIES = [
  "General",
  "HR",
  "IT",
  "IT & Security",
  "Legal",
  "Finance",
  "Payroll",
  "Leave",
  "Performance",
  "Health & Safety",
  "Code of Conduct",
  "Other",
];

export default function PoliciesTab() {
  const [policies,  setPolicies]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [ackModal,  setAckModal]  = useState(null);
  const [acks,      setAcks]      = useState([]);
  // Multi-file upload queue.  Each item:
  //   { file, title, category, version, description,
  //     requires_ack, status: 'pending'|'uploading'|'done'|'error',
  //     error: null }
  const [uploadQueue, setUploadQueue] = useState([]);
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState(null);
  const [dragOver,  setDragOver]  = useState(false);
  const fileInputRef              = useRef(null);
  const token = localStorage.getItem("token");

  // Parse one or more chosen files: derive title / category / version
  // from each filename and append to the queue.
  const handleFilesSelect = (files) => {
    const newItems = files.map(file => {
      const rawName = file.name
        .replace(/\.[^/.]+$/, "")
        .replace(/[-_]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      const title = rawName
        .split(" ")
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");

      const lower = rawName.toLowerCase();
      let category = "General";
      if (lower.includes("leave") || lower.includes("holiday") || lower.includes("absence"))
        category = "Leave";
      else if (lower.includes("pay") || lower.includes("salary") || lower.includes("payroll") || lower.includes("afc") || lower.includes("wage"))
        category = "Payroll";
      else if (lower.includes("performance") || lower.includes("appraisal") || lower.includes("review"))
        category = "Performance";
      else if (lower.includes("conduct") || lower.includes("disciplin") || lower.includes("grievance"))
        category = "HR";
      else if (lower.includes("health") || lower.includes("safety") || lower.includes("wellbeing"))
        category = "Health & Safety";
      else if (lower.includes("it") || lower.includes("data") || lower.includes("security") || lower.includes("gdpr"))
        category = "IT & Security";

      const versionMatch = rawName.match(/v(\d+[\.\d]*)/i);
      const version = versionMatch ? versionMatch[1] : "1.0";

      return {
        file,
        title,
        category,
        version,
        description: "",
        requires_ack: false,
        status: "pending",
        error: null,
      };
    });

    setUploadQueue(prev => [...prev, ...newItems]);
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/policies`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setPolicies(Array.isArray(data) ? data : []);
    } catch { setPolicies([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleUploadAll = async () => {
    const pending = uploadQueue.filter(item => item.status === "pending");
    if (!pending.length) return;

    setSaving(true); setMsg(null);
    for (let idx = 0; idx < uploadQueue.length; idx++) {
      const item = uploadQueue[idx];
      if (item.status !== "pending") continue;

      // Mark as uploading
      setUploadQueue(prev => prev.map((it, i) =>
        i === idx ? { ...it, status: "uploading" } : it
      ));

      try {
        const fd = new FormData();
        fd.append("file",         item.file);
        fd.append("title",        item.title);
        fd.append("category",     item.category);
        fd.append("version",      item.version);
        fd.append("description",  item.description || "");
        fd.append("requires_ack", item.requires_ack ? "true" : "false");

        const res = await fetch(`${API}/api/admin/policies`, {
          method:  "POST",
          headers: { Authorization: `Bearer ${token}` },
          body:    fd,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        setUploadQueue(prev => prev.map((it, i) =>
          i === idx ? { ...it, status: "done" } : it
        ));
      } catch (e) {
        setUploadQueue(prev => prev.map((it, i) =>
          i === idx ? { ...it, status: "error", error: e.message } : it
        ));
      }
    }
    setSaving(false);

    // Refresh the list once the batch is finished.
    await load();
  };

  const togglePublish = async (id, published) => {
    await fetch(`${API}/api/admin/policies/${id}`, {
      method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ published: !published }),
    });
    setPolicies(p => p.map(x => x.id === id ? { ...x, published: !published } : x));
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    const res = await fetch(`${API}/api/admin/policies/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setPolicies(p => p.filter(x => x.id !== id));
      toast.success("Policy deleted");
    } else {
      toast.error("Failed to delete policy");
    }
  };

  const viewAcks = async (policy) => {
    setAckModal(policy);
    try {
      const res = await fetch(`${API}/api/admin/policies/${policy.id}/acknowledgements`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAcks(Array.isArray(data) ? data : []);
    } catch { setAcks([]); }
  };

  // Group by category
  const grouped = policies.reduce((acc, p) => {
    const cat = p.category || "Other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-5">

      {/* Upload form */}
      {showForm && (
        <div className={`${card} p-5 space-y-4`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Upload Policy</p>

          {/* File upload — drag & drop zone (multi-file) */}
          <div>
            <label className={labelCls}>Document Files (PDF, DOCX, DOC, TXT)</label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const files = Array.from(e.dataTransfer.files).filter(f =>
                  [".pdf", ".docx", ".doc", ".txt"].some(ext =>
                    f.name.toLowerCase().endsWith(ext)
                  )
                );
                if (files.length) handleFilesSelect(files);
              }}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? "#00FF99" : "rgba(255,255,255,0.15)"}`,
                borderRadius: "12px",
                padding: "32px",
                textAlign: "center",
                cursor: "pointer",
                background: dragOver ? "rgba(0,255,153,0.05)" : "rgba(255,255,255,0.02)",
                transition: "all 0.2s ease",
                marginBottom: "20px",
              }}
            >
              <div style={{ fontSize: "32px", marginBottom: "8px" }}>📄</div>
              <div style={{ color: "#fff", fontSize: "14px", fontWeight: 500 }}>
                Drop your policy documents here
              </div>
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px", marginTop: "4px" }}>
                or click to browse — multiple files supported
              </div>
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "11px", marginTop: "8px" }}>
                Supports PDF, Word (DOC/DOCX), RTF, ODT, TXT, CSV
              </div>
              <div style={{
                color:        "rgba(255,180,0,0.7)",
                fontSize:     "11px",
                marginTop:    "6px",
                padding:      "6px 10px",
                background:   "rgba(255,180,0,0.06)",
                border:       "1px solid rgba(255,180,0,0.2)",
                borderRadius: "6px",
                display:      "inline-block",
              }}>
                ⚠️ Legacy .doc files may not index correctly.
                Save as .docx or PDF for best results.
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.doc,.odt,.rtf,.txt,.md,.csv"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                const files = Array.from(e.target.files);
                if (files.length) handleFilesSelect(files);
              }}
            />

            {/* Upload queue */}
            {uploadQueue.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{
                  display:        "flex",
                  justifyContent: "space-between",
                  alignItems:     "center",
                  marginBottom:   "8px",
                }}>
                  <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "12px" }}>
                    {uploadQueue.length} file(s) queued
                  </span>
                  <button
                    onClick={() => setUploadQueue([])}
                    style={{
                      background: "none",
                      border:     "none",
                      color:      "rgba(255,255,255,0.4)",
                      cursor:     "pointer",
                      fontSize:   "12px",
                    }}
                  >
                    Clear all
                  </button>
                </div>

                {uploadQueue.map((item, idx) => (
                  <div key={idx} style={{
                    background:
                      item.status === "done"  ? "rgba(0,255,153,0.05)" :
                      item.status === "error" ? "rgba(255,68,68,0.05)" :
                                                 "rgba(255,255,255,0.03)",
                    border: `1px solid ${
                      item.status === "done"  ? "rgba(0,255,153,0.2)" :
                      item.status === "error" ? "rgba(255,68,68,0.2)" :
                                                 "rgba(255,255,255,0.08)"
                    }`,
                    borderRadius: "10px",
                    padding:      "10px 14px",
                    marginBottom: "8px",
                  }}>
                    {/* Row 1: file info + status + remove */}
                    <div style={{
                      display:      "flex",
                      alignItems:   "center",
                      gap:          "10px",
                      marginBottom: "8px",
                    }}>
                      <span style={{ fontSize: "20px" }}>
                        {item.file.name.endsWith(".pdf")
                          ? "📕"
                          : item.file.name.endsWith(".docx")
                          ? "📘"
                          : "📄"}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: "#fff", fontSize: "12px", fontWeight: 500 }}>
                          {item.file.name}
                        </div>
                        <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "11px" }}>
                          {(item.file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>

                      {item.status === "uploading" && (
                        <span style={{ color: "#F59E0B", fontSize: "11px" }}>Uploading...</span>
                      )}
                      {item.status === "done" && (
                        <span style={{ color: "#00FF99", fontSize: "11px" }}>✓ Done</span>
                      )}
                      {item.status === "error" && (
                        <span style={{ color: "#EF4444", fontSize: "11px" }}>✗ {item.error}</span>
                      )}

                      {item.status === "pending" && (
                        <button
                          onClick={() =>
                            setUploadQueue(prev => prev.filter((_, i) => i !== idx))
                          }
                          style={{
                            background: "none",
                            border:     "none",
                            color:      "rgba(255,255,255,0.3)",
                            cursor:     "pointer",
                            fontSize:   "16px",
                            padding:    "0 4px",
                          }}
                        >×</button>
                      )}
                    </div>

                    {/* Row 2: editable fields (only while pending) */}
                    {item.status === "pending" && (
                      <div style={{
                        display:             "grid",
                        gridTemplateColumns: "1fr 1fr 80px",
                        gap:                 "8px",
                      }}>
                        <input
                          value={item.title}
                          onChange={(e) =>
                            setUploadQueue(prev => prev.map((it, i) =>
                              i === idx ? { ...it, title: e.target.value } : it
                            ))
                          }
                          placeholder="Title"
                          style={{
                            background:   "rgba(255,255,255,0.05)",
                            border:       "1px solid rgba(255,255,255,0.1)",
                            borderRadius: "6px",
                            padding:      "6px 10px",
                            color:        "#fff",
                            fontSize:     "12px",
                          }}
                        />
                        <select
                          value={item.category}
                          onChange={(e) =>
                            setUploadQueue(prev => prev.map((it, i) =>
                              i === idx ? { ...it, category: e.target.value } : it
                            ))
                          }
                          style={{
                            background:   "rgba(255,255,255,0.05)",
                            border:       "1px solid rgba(255,255,255,0.1)",
                            borderRadius: "6px",
                            padding:      "6px 10px",
                            color:        "#fff",
                            fontSize:     "12px",
                          }}
                        >
                          {CATEGORIES.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        <input
                          value={item.version}
                          onChange={(e) =>
                            setUploadQueue(prev => prev.map((it, i) =>
                              i === idx ? { ...it, version: e.target.value } : it
                            ))
                          }
                          placeholder="v1.0"
                          style={{
                            background:   "rgba(255,255,255,0.05)",
                            border:       "1px solid rgba(255,255,255,0.1)",
                            borderRadius: "6px",
                            padding:      "6px 10px",
                            color:        "#fff",
                            fontSize:     "12px",
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {msg && <p className={`text-xs ${msg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{msg.text}</p>}
          <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
            <button onClick={() => setShowForm(false)} className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={handleUploadAll}
              disabled={saving || !uploadQueue.some(i => i.status === "pending")}
              className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Uploading…" : "Upload All"}
            </button>
          </div>
        </div>
      )}

      {/* Policies list */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Policy Library</span>
          <button onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Plus size={12} /> Upload Policy
          </button>
        </div>

        <div className="p-4 space-y-5">
          {loading ? Array.from({length:3}).map((_,i) => <div key={i} className="h-12 rounded-lg bg-white/5 animate-pulse" />)
          : Object.keys(grouped).length === 0 ? (
            <p className="text-white/20 text-sm italic text-center py-6">No policies uploaded yet.</p>
          ) : Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <p className="text-xs text-white/30 uppercase tracking-wider mb-2">{cat}</p>
              <div className="space-y-2">
                {items.map(p => {
                  const isExpiring = p.review_date && new Date(p.review_date) < new Date(Date.now() + 30 * 86400000);
                  return (
                    <div key={p.id} className={`flex items-start justify-between gap-3 rounded-xl border px-4 py-3 ${
                      isExpiring ? "border-yellow-500/20 bg-yellow-500/3" : "border-white/5 bg-white/3"
                    }`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <FileText size={13} className="text-[#00ff90]/50 shrink-0" />
                          <p className="text-sm text-white/75 font-medium">{p.title}</p>
                          {!p.published && <span className="text-xs text-white/30 border border-white/10 px-2 py-0.5 rounded-full">Draft</span>}
                          {p.requires_ack && <span className="text-xs text-[#00ff90]/40 border border-[#00ff90]/15 px-2 py-0.5 rounded-full">Ack required</span>}
                          {isExpiring && <span className="text-xs text-yellow-400 border border-yellow-500/20 px-2 py-0.5 rounded-full">Review due</span>}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-white/30 flex-wrap">
                          {p.version && <span>v{p.version}</span>}
                          {p.uploaded_at && <span>Uploaded: {p.uploaded_at.split("T")[0]}</span>}
                          {p.review_date && <span>Review: {p.review_date}</span>}
                          {p.ack_count != null && <span>{p.ack_count} acknowledged</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {p.file_url && (
                          <a href={p.file_url} target="_blank" rel="noreferrer"
                            className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                            <Download size={12} />
                          </a>
                        )}
                        {p.requires_ack && (
                          <button onClick={() => viewAcks(p)}
                            className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                            <Users size={12} />
                          </button>
                        )}
                        <button onClick={() => togglePublish(p.id, p.published)}
                          className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors"
                          title={p.published ? "Unpublish" : "Publish"}>
                          {p.published ? <Eye size={12} /> : <EyeOff size={12} />}
                        </button>
                        <button onClick={() => del(p.id, p.title)}
                          className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Acknowledgement modal */}
      {ackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setAckModal(null)} />
          <div className="relative w-full max-w-lg rounded-xl border border-[#00ff90]/20 bg-[#0d0d0d] p-5 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <p className="font-semibold text-white">{ackModal.title} — Acknowledgements</p>
              <button onClick={() => setAckModal(null)} className="text-white/40 hover:text-white transition-colors"><X size={16} /></button>
            </div>
            {acks.length === 0 ? (
              <p className="text-white/20 text-sm italic text-center py-4">No acknowledgements yet.</p>
            ) : (
              <div className="space-y-2">
                {acks.map((a, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border border-white/5 bg-white/3 px-3 py-2">
                    <span className="text-sm text-white/70">{a.employee_name}</span>
                    <span className="text-xs text-[#00ff90]/60">{a.acknowledged_at?.split("T")[0]}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}